import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from 'react-query';
import api, { inboundProjectsAPI, customerAPI, inventoryAPI, documentsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import StepProgress from '../../components/StepProgress';
import { FiUpload, FiPlus, FiTrash2, FiAlertCircle, FiCheck, FiPackage } from 'react-icons/fi';

const defaultServiceCapabilities = {
  inbound_project: true
};
const defaultReceivingInstructions = {
  require_serial_numbers_on_receive: false,
  require_mac_addresses_on_receive: false,
  relabel_on_receive: false
};

const CreateInboundProject = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Items, 3: CSV Review (if CSV), 4: Final Review
  const [inputMethod, setInputMethod] = useState(null); // null, 'manual', or 'csv'
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [lines, setLines] = useState([]);
  const [activeLineIndex, setActiveLineIndex] = useState(0);
  const [lineErrors, setLineErrors] = useState({});
  const [skuValidation, setSkuValidation] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [skuTimeouts, setSkuTimeouts] = useState({});
  const [skuSuggestions, setSkuSuggestions] = useState({});
  const [activeSkuSuggestionLine, setActiveSkuSuggestionLine] = useState(null);
  const [isReviewValidating, setIsReviewValidating] = useState(false);
  const hasInitializedReceivingInstructions = useRef(false);
  const linesRef = useRef(lines);
  const skuRequestRef = useRef({});

  // Get customer warehouses
  const { data: warehousesData } = useQuery(
    'customerWarehouses',
    async () => {
      const response = await api.get('/customers/accessible-warehouses');
      return response.data?.data || [];
    },
    {
      staleTime: 300000, // 5 minutes
    }
  );
  
  const warehouses = Array.isArray(warehousesData) ? warehousesData : [];

  const { data: customerProfileResponse, isLoading: isLoadingProfile } = useQuery(
    ['customerProfileInboundManifest'],
    () => customerAPI.getProfile(),
    {
      staleTime: 300000
    }
  );

  const serviceCapabilities = {
    ...defaultServiceCapabilities,
    ...(customerProfileResponse?.data?.serviceCapabilities || customerProfileResponse?.data?.company?.service_capabilities || {})
  };
  const defaultReceivingInstructionsFromProfile = useMemo(() => ({
    ...defaultReceivingInstructions,
    ...(customerProfileResponse?.data?.receivingPreferences || customerProfileResponse?.data?.company?.receiving_preferences || {})
  }), [customerProfileResponse]);

  useEffect(() => {
    if (!isLoadingProfile && serviceCapabilities.inbound_project === false) {
      showError('Inbound manifests are not enabled for your company.');
      navigate('/customer/requests');
    }
  }, [isLoadingProfile, navigate, serviceCapabilities.inbound_project, showError]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(skuTimeouts).forEach(timeoutId => {
        if (timeoutId) clearTimeout(timeoutId);
      });
    };
  }, [skuTimeouts]);

  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  // Form validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required('Manifest name is required'),
    destination_warehouse_id: Yup.string().required('Destination warehouse is required'),
    expected_arrival: Yup.date().nullable(),
    notes: Yup.string()
  });

  // Form handling
  const formik = useFormik({
    initialValues: {
      name: '',
      destination_warehouse_id: '',
      expected_arrival: '',
      notes: '',
      receivingInstructions: defaultReceivingInstructions
    },
    validationSchema,
    onSubmit: async (values) => {
      // This will be handled by the create project mutation
    }
  });

  // Create project mutation
  const createProjectMutation = useMutation(
    (projectData) => inboundProjectsAPI.createProject(projectData),
    {
      onSuccess: (response) => {
        const requestId = response?.data?.request?.id;
        const projectId = response?.data?.project?.id;

        // Fire-and-forget: attach the original CSV/XLSX file to the request as a document
        if (requestId && csvFile) {
          documentsAPI.uploadDocument(requestId, csvFile, 'manifest', 'Uploaded manifest file').catch(() => {
            // Intentionally ignored -- document attachment is best-effort
          });
        }

        if (requestId) {
          navigate(`/customer/requests/${requestId}`);
          return;
        }

        if (projectId) {
          navigate(`/customer/inbound-projects/${projectId}`);
          return;
        }

        navigate('/customer/requests?type=inbound_manifest');
      },
      onError: (error) => {
        console.error('Error creating project:', error);
        showError(
          error.response?.data?.message || 'Failed to create project'
        );
      }
    }
  );

  useEffect(() => {
    if (!hasInitializedReceivingInstructions.current) {
      formik.setFieldValue('receivingInstructions', defaultReceivingInstructionsFromProfile, false);
      hasInitializedReceivingInstructions.current = true;
    }
  }, [defaultReceivingInstructionsFromProfile]);

  useEffect(() => {
    if (formik.values.destination_warehouse_id || warehouses.length !== 1) return;

    const [onlyWarehouse] = warehouses;
    if (!onlyWarehouse?.id) return;

    formik.setFieldValue('destination_warehouse_id', onlyWarehouse.id, false);
  }, [formik.values.destination_warehouse_id, warehouses]);

  // CSV upload mutation
  const uploadCsvMutation = useMutation(
    (formData) => inboundProjectsAPI.uploadProjectFile(formData),
    {
      onSuccess: (response) => {
        setCsvPreview(response.data);
        setLines(response.data.data || []);
        setStep(3); // Go to CSV review step
        showSuccess('CSV file processed successfully!');
      },
      onError: (error) => {
        console.error('Error uploading CSV:', error);
        showError(
          error.response?.data?.message || 'Failed to process CSV file'
        );
      }
    }
  );

  if (isLoadingProfile) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const normalizeSku = (sku) => String(sku || '').trim().toLowerCase();

  const getInventorySearchResults = async (sku) => {
    const normalizedSku = String(sku || '').trim().toLowerCase();
    if (!normalizedSku) return [];

    const results = await inventoryAPI.searchInventory(normalizedSku);
    return Array.isArray(results) ? results : results?.data || [];
  };

  const findExactSkuMatch = async (sku) => {
    const normalizedSku = normalizeSku(sku);
    if (!normalizedSku) return null;

    const items = await getInventorySearchResults(normalizedSku);
    return items.find((item) => String(item.productSku || '').trim().toLowerCase() === normalizedSku) || null;
  };

  const applyExistingSkuMatch = (line, matchedItem) => ({
    ...line,
    item_name: line.item_name || matchedItem.productName || '',
    original_description: line.original_description || matchedItem.description || '',
    vendor: line.vendor || matchedItem.vendor || ''
  });

  const selectSkuSuggestion = (lineIndex, item) => {
    const selectedSku = item.productSku || '';
    setLines(prevLines => {
      const updatedLines = [...prevLines];
      updatedLines[lineIndex] = applyExistingSkuMatch(
        { ...updatedLines[lineIndex], original_sku: selectedSku },
        item
      );
      linesRef.current = updatedLines;
      return updatedLines;
    });
    setSkuValidation(prev => ({ ...prev, [lineIndex]: 'valid' }));
    setSkuSuggestions(prev => ({ ...prev, [lineIndex]: [] }));
    setActiveSkuSuggestionLine(null);
  };

  // Validate SKU against customer's existing SKUs and populate data
  const validateSku = async (sku, lineIndex) => {
    const normalizedSku = normalizeSku(sku);
    const requestId = (skuRequestRef.current[lineIndex] || 0) + 1;
    skuRequestRef.current[lineIndex] = requestId;

    if (!normalizedSku) {
      setSkuValidation(prev => ({ ...prev, [lineIndex]: null }));
      setSkuSuggestions(prev => ({ ...prev, [lineIndex]: [] }));
      return;
    }

    if (normalizedSku.length < 3) {
      setSkuValidation(prev => ({ ...prev, [lineIndex]: null }));
      setSkuSuggestions(prev => ({ ...prev, [lineIndex]: [] }));
      return;
    }

    try {
      const items = await getInventorySearchResults(normalizedSku);
      if (skuRequestRef.current[lineIndex] !== requestId || normalizeSku(linesRef.current[lineIndex]?.original_sku) !== normalizedSku) {
        return;
      }

      const exactMatches = items.filter((item) => normalizeSku(item.productSku) === normalizedSku);
      const matchedItem = exactMatches.length === 1 ? exactMatches[0] : null;
      setSkuSuggestions(prev => ({
        ...prev,
        [lineIndex]: matchedItem ? [] : items
      }));
      
      if (matchedItem) {
        // SKU exists - populate data
        setSkuValidation(prev => ({ 
          ...prev, 
          [lineIndex]: 'valid'
        }));
        
        // Auto-populate fields with existing inventory data
        setLines(prevLines => {
          if (normalizeSku(prevLines[lineIndex]?.original_sku) !== normalizedSku) return prevLines;
          const updatedLines = [...prevLines];
          updatedLines[lineIndex] = applyExistingSkuMatch(updatedLines[lineIndex], matchedItem);
          linesRef.current = updatedLines;
          return updatedLines;
        });
      } else {
        // SKU doesn't exist yet — mark as unmatched (will be created on submit)
        setSkuValidation(prev => ({
          ...prev,
          [lineIndex]: 'unmatched'
        }));
      }
    } catch (error) {
      console.error('Error validating SKU:', error);
      setSkuValidation(prev => ({ 
        ...prev, 
        [lineIndex]: 'error'
      }));
    }
  };

  const validateAllSkusForReview = async () => {
    const nextValidation = {};
    const nextLines = [...lines];
    let hasErrors = false;

    for (let index = 0; index < nextLines.length; index += 1) {
      const line = nextLines[index];
      const sku = String(line.original_sku || '').trim();

      if (!sku) {
        nextValidation[index] = 'invalid';
        hasErrors = true;
        continue;
      }

      try {
        const matchedItem = await findExactSkuMatch(sku);
        if (matchedItem) {
          nextValidation[index] = 'valid';
          nextLines[index] = {
            ...nextLines[index],
            item_name: nextLines[index].item_name || matchedItem.productName || '',
            original_description: nextLines[index].original_description || matchedItem.description || '',
            vendor: nextLines[index].vendor || matchedItem.vendor || ''
          };
        } else {
          // New SKU — allow it through, backend will find-or-create
          nextValidation[index] = 'unmatched';
        }
      } catch (error) {
        console.error('Error validating SKU for review:', error);
        nextValidation[index] = 'error';
        hasErrors = true;
      }
    }

    setSkuValidation(nextValidation);
    setLines(nextLines);
    return !hasErrors;
  };

  // Handle CSV file selection
  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
    }
  };

  // Upload CSV file
  const handleCsvUpload = () => {
    if (!csvFile) return;

    const formData = new FormData();
    formData.append('file', csvFile);
    uploadCsvMutation.mutate(formData);
  };

  // Add manual line
  const addManualLine = () => {
    if (lines.length > 0 && !validateManualLine(activeLineIndex)) {
      showError('Save the current item before adding another item.');
      return;
    }

    // Auto-collapse previous items if they have valid data
    const updatedExpansion = { ...expandedItems };
    lines.forEach((line, index) => {
      if (line.original_sku && line.qty_expected > 0) {
        updatedExpansion[index] = false;
      }
    });

    const newLine = {
      vendor: '',
      original_sku: '',
      item_name: '',
      original_description: '',
      qty_expected: 1,
      expected_package_type: 'Each',
      gross_weight_lbs: null,
      declared_unit_value: null,
      notes: ''
    };
    const newIndex = lines.length;
    setLines([...lines, newLine]);
    linesRef.current = [...lines, newLine];
    setActiveLineIndex(newIndex);
    
    // Expand the newly added item
    updatedExpansion[newIndex] = true;
    setExpandedItems(updatedExpansion);
  };

  // Toggle item expansion
  const toggleItemExpansion = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getManualLineErrors = (line) => {
    const errors = {};
    if (!String(line?.original_sku || '').trim()) errors.original_sku = 'SKU is required';
    if (!line?.qty_expected || Number(line.qty_expected) < 1) errors.qty_expected = 'Quantity is required';
    return errors;
  };

  const validateManualLine = (index) => {
    const errors = getManualLineErrors(linesRef.current[index]);
    setLineErrors(prev => ({ ...prev, [index]: errors }));
    return Object.keys(errors).length === 0;
  };

  const saveManualLine = (index) => {
    if (!validateManualLine(index)) return false;
    setExpandedItems(prev => ({ ...prev, [index]: false }));
    return true;
  };

  const getLineStatus = (line, index) => {
    if (Object.keys(lineErrors[index] || {}).length > 0) return 'Needs value';
    return getManualLineErrors(line).original_sku || getManualLineErrors(line).qty_expected ? 'Draft' : 'Saved';
  };

  // Update line
  const updateLine = (index, field, value) => {
    const updatedLines = [...lines];
    updatedLines[index] = { ...updatedLines[index], [field]: value };
    linesRef.current = updatedLines;
    setLines(updatedLines);
    if (lineErrors[index]?.[field]) {
      const nextErrors = { ...(lineErrors[index] || {}) };
      delete nextErrors[field];
      setLineErrors(prev => ({ ...prev, [index]: nextErrors }));
    }

    // Validate SKU if it's being updated (with debouncing)
    if (field === 'original_sku') {
      setSkuValidation(prev => ({ ...prev, [index]: null }));
      setActiveSkuSuggestionLine(index);
      // Clear existing timeout for this line
      if (skuTimeouts[index]) {
        clearTimeout(skuTimeouts[index]);
      }
      
      // Set new timeout for SKU validation
      const timeoutId = setTimeout(() => {
        validateSku(value, index);
      }, 500); // 500ms delay
      
      setSkuTimeouts(prev => ({
        ...prev,
        [index]: timeoutId
      }));
    }
  };

  // Remove line
  const removeLine = (index) => {
    const updatedLines = lines.filter((_, i) => i !== index);
    linesRef.current = updatedLines;
    setLines(updatedLines);
    setActiveLineIndex(Math.max(0, Math.min(activeLineIndex, updatedLines.length - 1)));
    // Remove SKU validation for this line
    const newValidation = { ...skuValidation };
    delete newValidation[index];
    setSkuValidation(newValidation);
    const newSuggestions = { ...skuSuggestions };
    delete newSuggestions[index];
    setSkuSuggestions(newSuggestions);
    const newLineErrors = { ...lineErrors };
    delete newLineErrors[index];
    setLineErrors(newLineErrors);
  };

  // Submit project
  const handleSubmit = () => {
    if (!formik.isValid) {
      formik.validateForm();
      return;
    }

    if (lines.length === 0) {
      showError('Please add at least one line item');
      return;
    }

    // Check for required SKUs
    const missingSkus = lines.filter(line => !line.original_sku);
    if (missingSkus.length > 0) {
      showError('Each line must have a SKU');
      return;
    }

    const projectData = {
      ...formik.values,
      lines
    };

    createProjectMutation.mutate(projectData);
  };

  const handleProceedToReview = async () => {
    if (lines.length === 0) return;

    if (inputMethod === 'csv') {
      setStep(3);
      return;
    }

    if (!validateManualLine(activeLineIndex)) {
      showError('Save the current item before reviewing.');
      return;
    }

    setIsReviewValidating(true);
    await validateAllSkusForReview();
    setIsReviewValidating(false);

    setStep(3);
  };

  // Get SKU validation icon
  const getSkuValidationIcon = (status) => {
    switch (status) {
      case 'valid':
        return <FiCheck className="w-4 h-4 text-green-500" title="Existing SKU" />;
      case 'unmatched':
        return <FiAlertCircle className="w-4 h-4 text-yellow-500" title="New SKU — will be created" />;
      case 'error':
        return <FiAlertCircle className="w-4 h-4 text-red-500" title="Validation error" />;
      default:
        return null;
    }
  };

  const getReviewLineStatus = (line, index) => {
    if (inputMethod === 'csv') {
      return line.match_status === 'matched' ? 'valid' : 'unmatched';
    }
    return skuValidation[index] || null;
  };

  const getCustomerFacingStatusLabel = (line, index) => {
    const status = getReviewLineStatus(line, index);
    return status === 'valid' ? 'Existing SKU' : 'New SKU';
  };

  const existingSkuCount = lines.filter((line, index) => getReviewLineStatus(line, index) === 'valid').length;
  const newSkuCount = Math.max(lines.length - existingSkuCount, 0);

  return (
    <div className="py-6">
      <div className="max-w-[54rem] mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Inbound Manifest</h1>
          <p className="text-gray-600">Set up expected incoming inventory</p>
        </div>
        <button
          onClick={() => navigate('/customer/requests?type=incoming,crossdock')}
          className="text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
      </div>

      {/* Progress Steps */}
      <StepProgress
        steps={inputMethod === 'csv'
          ? [{ id: 1, name: 'Basic Info' }, { id: 2, name: 'Add Items' }, { id: 3, name: 'CSV Review' }, { id: 4, name: 'Review' }]
          : [{ id: 1, name: 'Basic Info' }, { id: 2, name: 'Add Items' }, { id: 3, name: 'Review' }]
        }
        currentStep={step}
      />

      {/* Step 1: Basic Information */}
      {step === 1 && (
        <div className="bg-white p-6 rounded-lg shadow border">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Manifest Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Manifest Name *
              </label>
              <input
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter manifest name"
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  formik.touched.name && formik.errors.name
                    ? 'border-red-500'
                    : 'border-gray-300'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Provide a name for this manifest, for example your PO number.
              </p>
              {formik.touched.name && formik.errors.name && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.name}</p>
              )}
            </div>

            {/* Destination Warehouse */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destination Warehouse *
              </label>
              <select
                name="destination_warehouse_id"
                value={formik.values.destination_warehouse_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`block w-full border rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  formik.touched.destination_warehouse_id && formik.errors.destination_warehouse_id
                    ? 'border-red-500'
                    : 'border-gray-300'
                }`}
              >
                <option value="">Select destination warehouse</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.name} - {warehouse.address_line1 || ''}{warehouse.address_line2 ? `, ${warehouse.address_line2}` : ''}, {warehouse.city}
                  </option>
                ))}
              </select>
              
              {/* Selected Warehouse Confirmation */}
              {formik.values.destination_warehouse_id && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Destination: {warehouses.find(w => w.id === formik.values.destination_warehouse_id)?.name}
                      </p>
                      <p className="text-xs text-green-700">
                        {(() => { const wh = warehouses.find(w => w.id === formik.values.destination_warehouse_id); return wh ? `${wh.address_line1}${wh.address_line2 ? `, ${wh.address_line2}` : ''}, ${wh.city}, ${wh.state_province} ${wh.postal_code || ''}${wh.country ? `, ${wh.country}` : ''}`.trim() : ''; })()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {formik.touched.destination_warehouse_id && formik.errors.destination_warehouse_id && (
                <p className="mt-1 text-sm text-red-600">{formik.errors.destination_warehouse_id}</p>
              )}
            </div>

            {/* Estimated Arrival */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Arrival Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="expected_arrival"
                  value={formik.values.expected_arrival}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent cursor-pointer"
                />
              </div>
            </div>


          </div>

          {/* Notes */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Receiving Service Instructions
            </label>
            <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-4">
              {[
                ['require_serial_numbers_on_receive', 'Record serial numbers on receipt'],
                ['require_mac_addresses_on_receive', 'Record MAC addresses on receipt'],
                ['relabel_on_receive', 'Relabel units on receipt']
              ].map(([key, label]) => (
                <label key={key} className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!formik.values.receivingInstructions?.[key]}
                    onChange={(e) => formik.setFieldValue(`receivingInstructions.${key}`, e.target.checked)}
                    className="mr-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  {label}
                </label>
              ))}
              <p className="text-xs text-gray-500">
                These instructions apply to this manifest only and can create receiving service charges when processed.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formik.values.notes}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              rows={3}
              placeholder="Additional notes about this project"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!formik.values.name || !formik.values.destination_warehouse_id}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
            >
              Next: Add Items
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Add Items */}
      {step === 2 && (
        <div className="space-y-6">
          {!inputMethod && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Add Items</h2>
              <p className="text-sm text-gray-600 mb-6">Choose how you want to add line items to this inbound manifest.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setInputMethod('manual');
                    if (lines.length === 0) addManualLine();
                  }}
                  className="rounded-lg border border-gray-200 p-5 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                >
                  <FiPlus className="w-6 h-6 text-indigo-600 mb-3" />
                  <p className="font-semibold text-gray-900">Manual Entry</p>
                  <p className="text-sm text-gray-600 mt-1">Add SKUs one at a time with quantities and optional details.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setInputMethod('csv')}
                  className="rounded-lg border border-gray-200 p-5 text-left hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                >
                  <FiUpload className="w-6 h-6 text-indigo-600 mb-3" />
                  <p className="font-semibold text-gray-900">CSV Upload</p>
                  <p className="text-sm text-gray-600 mt-1">Upload a spreadsheet when you have many manifest lines.</p>
                </button>
              </div>
            </div>
          )}

          {inputMethod === 'csv' && (
            <div className="bg-white p-6 rounded-lg shadow border">
              <button
                type="button"
                onClick={() => setInputMethod(null)}
                className="mb-4 text-sm text-indigo-600 hover:text-indigo-800"
              >
                &larr; Back to entry selection
              </button>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="mb-4">
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-800 font-medium">Choose CSV file</span>
                    <input type="file" accept=".csv,.xlsx,.xls" onChange={handleCsvFileChange} className="hidden" />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Supported formats: CSV, Excel (.xlsx, .xls)</p>
                </div>
                {csvFile && (
                  <div className="bg-gray-50 rounded-md p-4 mb-4">
                    <p className="text-sm font-medium text-gray-900">Selected: {csvFile.name}</p>
                    <p className="text-sm text-gray-500">Size: {(csvFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                )}
                <button
                  onClick={handleCsvUpload}
                  disabled={!csvFile || uploadCsvMutation.isLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
                >
                  {uploadCsvMutation.isLoading ? <LoadingSpinner /> : 'Upload & Process'}
                </button>
              </div>
            </div>
          )}

          {inputMethod === 'manual' && lines[activeLineIndex] && (
            <div className="bg-white rounded-lg shadow border overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr]">
                <aside className="border-b lg:border-b-0 lg:border-r border-gray-200 bg-gray-50">
                  <div className="p-4 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-900">Items <span className="ml-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600">{lines.length}</span></h2>
                    <button type="button" onClick={() => setInputMethod(null)} className="text-xs text-indigo-600 hover:text-indigo-800">Change method</button>
                  </div>
                  <div className="px-4 pb-4">
                    <button
                      type="button"
                      onClick={addManualLine}
                      className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                      + Add Item
                    </button>
                  </div>
                  <div className="space-y-2 px-3 pb-4">
                    {lines.map((line, index) => {
                      const status = getLineStatus(line, index);
                      const isActive = index === activeLineIndex;
                      return (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setActiveLineIndex(index)}
                          className={`w-full rounded-lg border p-3 text-left transition-colors ${
                            isActive ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="mt-0.5 h-6 w-6 rounded-full bg-gray-100 text-center text-xs font-semibold leading-6 text-gray-600">{index + 1}</span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-gray-900">{line.item_name || line.original_sku || `Line Item ${index + 1}`}</span>
                              <span className="block truncate text-xs text-gray-500">{line.original_sku || 'No SKU'}{line.qty_expected ? ` | ${line.qty_expected} ${line.expected_package_type || 'ea'}` : ''}</span>
                            </span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                              status === 'Saved' ? 'bg-green-100 text-green-700' : status === 'Needs value' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {status}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </aside>

                <section className="p-6">
                  {(() => {
                    const line = lines[activeLineIndex];
                    const errors = lineErrors[activeLineIndex] || {};
                    return (
                      <div className="space-y-5">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-lg font-semibold text-gray-900">Line Item {activeLineIndex + 1} of {lines.length}</h3>
                              {Object.keys(errors).length > 0 && <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">Needs value</span>}
                            </div>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Required</p>
                          </div>
                          <button type="button" onClick={() => removeLine(activeLineIndex)} className="text-sm font-medium text-red-600 hover:text-red-700">
                            Remove
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="relative">
                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">SKU <span className="text-red-500">*</span></label>
                            <div className="relative">
                              <input
                                type="text"
                                value={line.original_sku}
                                onChange={(e) => updateLine(activeLineIndex, 'original_sku', e.target.value)}
                                placeholder="Enter SKU"
                                className={`w-full px-3 py-2 pr-8 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.original_sku ? 'border-red-400' : 'border-gray-300'}`}
                              />
                              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">{getSkuValidationIcon(skuValidation[activeLineIndex])}</div>
                            </div>
                            {errors.original_sku && <p className="text-xs text-red-600 mt-1">{errors.original_sku}</p>}
                            {skuValidation[activeLineIndex] === 'unmatched' && <p className="text-xs text-yellow-600 mt-1">New SKU will be created in your inventory</p>}
                            {skuValidation[activeLineIndex] === 'valid' && <p className="text-xs text-green-600 mt-1">Matched existing SKU</p>}
                            {activeSkuSuggestionLine === activeLineIndex && (skuSuggestions[activeLineIndex] || []).length > 0 && (
                              <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                                {(skuSuggestions[activeLineIndex] || []).map((item) => (
                                  <button key={item.id} type="button" onClick={() => selectSkuSuggestion(activeLineIndex, item)} className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50">
                                    <span className="text-gray-900">{item.productSku}</span>
                                    {item.productName && <span className="ml-2 text-gray-600">{item.productName}</span>}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Item Name <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                            <input type="text" value={line.item_name || ''} onChange={(e) => updateLine(activeLineIndex, 'item_name', e.target.value)} placeholder="Item name" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Quantity <span className="text-red-500">*</span></label>
                            <input type="number" min="1" value={line.qty_expected} onChange={(e) => updateLine(activeLineIndex, 'qty_expected', parseInt(e.target.value) || '')} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.qty_expected ? 'border-red-400' : 'border-gray-300'}`} />
                            {errors.qty_expected && <p className="text-xs text-red-600 mt-1">{errors.qty_expected}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Expected Package Type <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                            <select value={line.expected_package_type || 'Each'} onChange={(e) => updateLine(activeLineIndex, 'expected_package_type', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                              <option value="Each">Each</option>
                              <option value="Carton">Carton</option>
                              <option value="Pallet">Pallet</option>
                            </select>
                          </div>
                        </div>

                        <div className="border-t border-gray-200 pt-4">
                          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Optional Details</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Vendor <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                              <input type="text" value={line.vendor || ''} onChange={(e) => updateLine(activeLineIndex, 'vendor', e.target.value)} placeholder="Vendor name" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Approx. Value ($) <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                              <input type="number" step="1" min="0" value={line.declared_unit_value || ''} onChange={(e) => updateLine(activeLineIndex, 'declared_unit_value', e.target.value === '' ? null : parseFloat(e.target.value))} placeholder="Per unit value" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Description <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                              <input type="text" value={line.original_description || ''} onChange={(e) => updateLine(activeLineIndex, 'original_description', e.target.value)} placeholder="Product description" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Project Name <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                              <input type="text" value={line.project_name || ''} onChange={(e) => updateLine(activeLineIndex, 'project_name', e.target.value)} placeholder="Project name" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Project Code <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                              <input type="text" value={line.project_code || ''} onChange={(e) => updateLine(activeLineIndex, 'project_code', e.target.value)} placeholder="Project code" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">Notes <span className="font-normal normal-case text-gray-400">(Optional)</span></label>
                              <input type="text" value={line.notes || ''} onChange={(e) => updateLine(activeLineIndex, 'notes', e.target.value)} placeholder="Optional notes" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <button type="button" onClick={() => saveManualLine(activeLineIndex)} className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">
                            Save Item
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </section>
              </div>
            </div>
          )}

          {false && (
          <>
          {/* Input Method Selection */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Items</h2>
            
            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => setInputMethod('manual')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  inputMethod === 'manual'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Manual Entry
              </button>
              <button
                onClick={() => setInputMethod('csv')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  inputMethod === 'csv'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                CSV Upload
              </button>
            </div>

            {/* CSV Upload */}
            {inputMethod === 'csv' && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <FiUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <div className="mb-4">
                  <label className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-800 font-medium">
                      Choose CSV file
                    </span>
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleCsvFileChange}
                      className="hidden"
                    />
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    Supported formats: CSV, Excel (.xlsx, .xls)
                  </p>
                </div>
                
                {csvFile && (
                  <div className="bg-gray-50 rounded-md p-4 mb-4">
                    <p className="text-sm font-medium text-gray-900">
                      Selected: {csvFile.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      Size: {(csvFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                )}

                <button
                  onClick={handleCsvUpload}
                  disabled={!csvFile || uploadCsvMutation.isLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
                >
                  {uploadCsvMutation.isLoading ? (
                    <div className="flex items-center">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    'Upload & Process'
                  )}
                </button>

                <div className="mt-4 text-left">
                  <h4 className="font-medium text-gray-900 mb-2">CSV Format:</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Field</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Required</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-700">Example</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        <tr>
                          <td className="py-2 px-3 font-mono">vendor</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">Acme Co</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">original_sku*</td>
                          <td className="py-2 px-3 text-red-600 font-medium">Required</td>
                          <td className="py-2 px-3 text-gray-600">ABC-123</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">item_name</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">Widget A</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">original_description</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">High quality widget</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">qty_expected*</td>
                          <td className="py-2 px-3 text-red-600 font-medium">Required</td>
                          <td className="py-2 px-3 text-gray-600">500</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">expected_package_type</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">Carton, Pallet, Each</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">declared_unit_value</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">25.00 (approx. per-unit value for insurance)</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">project_name</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">Bank of Canada</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">project_code</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">BOC12345</td>
                        </tr>
                        <tr>
                          <td className="py-2 px-3 font-mono">notes</td>
                          <td className="py-2 px-3 text-gray-600">Optional</td>
                          <td className="py-2 px-3 text-gray-600">Handle with care</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <p className="text-sm text-blue-800 mb-2">
                        <strong>Sample CSV:</strong>
                      </p>
                      <div className="bg-white rounded p-2 border overflow-x-auto">
                        <code className="text-xs whitespace-nowrap block">
                          vendor,original_sku,item_name,original_description,qty_expected,expected_package_type,declared_unit_value,project_name,project_code,notes
                        </code>
                        <code className="text-xs whitespace-nowrap block text-gray-600">
                          Acme Co,ABC-123,Widget A,High quality widget,500,Carton,Handle with care
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Manual Entry */}
            {inputMethod === 'manual' && (
              <div>
                {lines.length === 0 && (
                  <div className="text-center py-8">
                    <FiPackage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No items added yet</p>
                    <button
                      onClick={addManualLine}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md flex items-center mx-auto"
                    >
                      <FiPlus className="w-4 h-4 mr-2" />
                      Add First Item
                    </button>
                  </div>
                )}

                {lines.length > 0 && (
                  <div className="space-y-4">
                    {lines.map((line, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg">
                        {/* Collapsed View */}
                        {!expandedItems[index] && (
                          <div className="p-4">
                            <div className="flex justify-between items-center">
                              <div className="flex-1">
                                <h4 className="font-medium text-gray-900">
                                  Line Item #{index + 1} - {line.original_sku || 'No SKU'}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {line.item_name && `${line.item_name} • `}
                                  Qty: {line.qty_expected} • {line.expected_package_type || 'Each'}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => toggleItemExpansion(index)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Expand item"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => removeLine(index)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Delete item"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Expanded View */}
                        {expandedItems[index] && (
                          <div className="p-4">
                            <div className="flex justify-between items-start mb-4">
                              <h4 className="font-medium text-gray-900">Line Item #{index + 1}</h4>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => toggleItemExpansion(index)}
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Collapse item"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => removeLine(index)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Delete item"
                                >
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>

                        <div className="space-y-4">
                          {/* Line 1: SKU | Item Name */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* SKU */}
                            <div className="relative">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                SKU *
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={line.original_sku}
                                  onChange={(e) => updateLine(index, 'original_sku', e.target.value)}
                                  placeholder="Enter SKU"
                                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                />
                                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                  {getSkuValidationIcon(skuValidation[index])}
                                </div>
                              </div>
                              {skuValidation[index] === 'unmatched' && (
                                <p className="text-xs text-yellow-600 mt-1">New SKU — will be created in your inventory</p>
                              )}
                              {skuValidation[index] === 'valid' && (
                                <p className="text-xs text-green-600 mt-1">Matched existing SKU</p>
                              )}
                              {activeSkuSuggestionLine === index && (skuSuggestions[index] || []).length > 0 && (
                                <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                                  {(skuSuggestions[index] || []).map((item) => (
                                    <button
                                      key={item.id}
                                      type="button"
                                      onClick={() => selectSkuSuggestion(index, item)}
                                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                                    >
                                      <span className="font-mono text-gray-900">{item.productSku}</span>
                                      {item.productName && (
                                        <span className="ml-2 text-gray-600">{item.productName}</span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Item Name */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Item Name
                              </label>
                              <input
                                type="text"
                                value={line.item_name || ''}
                                onChange={(e) => updateLine(index, 'item_name', e.target.value)}
                                placeholder="Item name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>
                          </div>

                          {/* Line 2: Quantity | Expected Package Type */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Quantity */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantity *
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={line.qty_expected}
                                onChange={(e) => updateLine(index, 'qty_expected', parseInt(e.target.value) || 1)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>

                            {/* Expected Package Type */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Expected Package Type
                              </label>
                              <select
                                value={line.expected_package_type || 'Each'}
                                onChange={(e) => updateLine(index, 'expected_package_type', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              >
                                <option value="Each">Each</option>
                                <option value="Carton">Carton</option>
                                <option value="Pallet">Pallet</option>
                              </select>
                            </div>
                          </div>

                          {/* Line 3: Vendor | Description */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Vendor */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Vendor
                              </label>
                              <input
                                type="text"
                                value={line.vendor || ''}
                                onChange={(e) => updateLine(index, 'vendor', e.target.value)}
                                placeholder="Vendor name"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>

                            {/* Description */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Description
                              </label>
                              <input
                                type="text"
                                value={line.original_description || ''}
                                onChange={(e) => updateLine(index, 'original_description', e.target.value)}
                                placeholder="Product description"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>

                            {/* Declared Value */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                                Approx. Value ($)
                                <span className="relative group ml-1">
                                  <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path strokeLinecap="round" strokeWidth="2" d="M12 16v-4m0-4h.01"/></svg>
                                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                    Approximate value for insurance purposes
                                  </span>
                                </span>
                              </label>
                              <input
                                type="number"
                                step="1"
                                min="0"
                                value={line.declared_unit_value || ''}
                                onChange={(e) => updateLine(index, 'declared_unit_value', parseFloat(e.target.value) || null)}
                                placeholder="Per unit value"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>
                          </div>

                          {/* Line 4: Project Name / Code */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                              <input
                                type="text"
                                value={line.project_name || ''}
                                onChange={(e) => updateLine(index, 'project_name', e.target.value)}
                                placeholder="e.g., Bank of Canada"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Project Code</label>
                              <input
                                type="text"
                                value={line.project_code || ''}
                                onChange={(e) => updateLine(index, 'project_code', e.target.value)}
                                placeholder="e.g., BOC12345"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              />
                            </div>
                          </div>

                          {/* Line 5: Notes (full width) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                            <input
                              type="text"
                              value={line.notes || ''}
                              onChange={(e) => updateLine(index, 'notes', e.target.value)}
                              placeholder="Optional notes"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                          </div>
                        )}
                      </div>
                    ))}

                    <button
                      onClick={addManualLine}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 hover:bg-blue-50 transition-colors"
                    >
                      <FiPlus className="w-5 h-5 text-gray-400 mx-auto mb-2" />
                      <span className="text-gray-600">Add Another Item</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          </>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleProceedToReview}
              disabled={lines.length === 0}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors"
            >
              {isReviewValidating ? 'Validating SKUs...' : (inputMethod === 'csv' ? 'Review CSV' : 'Next: Review')}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: CSV Review (only for CSV uploads) */}
      {step === 3 && inputMethod === 'csv' && csvPreview && (
        <div className="space-y-6">
          {/* CSV Validation Summary */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">CSV Validation Results</h2>
            
            {csvPreview.validation && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiPackage className="w-6 h-6 text-blue-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Total Lines</p>
                      <p className="text-2xl font-bold text-blue-600">{csvPreview.validation.totalLines}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiCheck className="w-6 h-6 text-green-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Matched SKUs</p>
                      <p className="text-2xl font-bold text-green-600">{csvPreview.validation.matchedLines}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiAlertCircle className="w-6 h-6 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">New Items</p>
                      <p className="text-2xl font-bold text-yellow-600">{csvPreview.validation.unmatchedLines}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Duplicate SKU Info */}
            {csvPreview.validation?.duplicateSkus > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <FiAlertCircle className="w-5 h-5 text-amber-500 mr-2 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-amber-800">Duplicate SKUs Found</h3>
                    <p className="text-sm text-amber-700 mb-2">
                      These duplicate SKU rows are allowed. The system will reuse one inventory item per SKU and keep each manifest line separate for its project allocation:
                    </p>
                    <ul className="list-disc list-inside text-sm text-amber-700">
                      {csvPreview.validation.duplicateDetails?.map((dup, index) => (
                        <li key={index}>
                          <strong>{dup.sku}</strong> appears {dup.count} times (lines: {dup.lines.join(', ')})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Action Items */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">What happens next?</h3>
              <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                <li><strong>{csvPreview.validation?.matchedLines || 0} items</strong> will be linked to your existing inventory</li>
                <li><strong>{csvPreview.validation?.unmatchedLines || 0} new items</strong> will be created in your inventory</li>
                <li>All items will be added to your inbound manifest for tracking</li>
              </ul>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Item Details</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Line</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lines.map((line, index) => (
                    <tr key={index} className={line.match_status === 'matched' ? 'bg-green-50' : 'bg-yellow-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {line.line_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {line.original_sku}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {line.item_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {line.qty_expected}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {line.match_status === 'matched' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FiCheck className="w-3 h-3 mr-1" />
                            Existing Item
                          </span>
                        )}
                        {line.match_status === 'unmatched' && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <FiPlus className="w-3 h-3 mr-1" />
                            New Item
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md transition-colors"
            >
              Back to Items
            </button>
            <button
              onClick={() => setStep(4)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-md transition-colors"
            >
              Continue to Review
            </button>
          </div>
        </div>
      )}

      {/* Final Review & Submit */}
      {(step === 4 || (step === 3 && inputMethod === 'manual')) && (
        <div className="space-y-6">
          {/* Project Summary */}
          <div className="bg-white p-6 rounded-lg shadow border">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Summary</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Project Details</h3>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Name:</dt>
                    <dd className="text-gray-900">{formik.values.name}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Warehouse:</dt>
                    <dd className="text-gray-900">
                      {warehouses.find(w => w.id === formik.values.destination_warehouse_id)?.name || 'N/A'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Estimated Arrival:</dt>
                    <dd className="text-gray-900">
                      {formik.values.expected_arrival || 'Not specified'}
                    </dd>
                  </div>
                </dl>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">Items Summary</h3>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total Items:</dt>
                    <dd className="text-gray-900">{lines.length}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Total Quantity:</dt>
                    <dd className="text-gray-900">
                      {lines.reduce((sum, line) => sum + (line.qty_expected || 0), 0)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">Existing SKUs:</dt>
                    <dd className="text-gray-900">{existingSkuCount}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-600">New SKUs:</dt>
                    <dd className="text-gray-900">{newSkuCount}</dd>
                  </div>
                </dl>
              </div>
            </div>

            {formik.values.notes && (
              <div className="mt-4">
                <h3 className="font-medium text-gray-900 mb-2">Notes</h3>
                <p className="text-sm text-gray-600">{formik.values.notes}</p>
              </div>
            )}

            {Object.values(formik.values.receivingInstructions || {}).some(Boolean) && (
              <div className="mt-4">
                <h3 className="font-medium text-gray-900 mb-2">Receiving Services Requested</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  {formik.values.receivingInstructions.require_serial_numbers_on_receive && <p>Record serial numbers on receipt</p>}
                  {formik.values.receivingInstructions.require_mac_addresses_on_receive && <p>Record MAC addresses on receipt</p>}
                  {formik.values.receivingInstructions.relabel_on_receive && <p>Relabel units on receipt</p>}
                </div>
              </div>
            )}
          </div>

          {/* Items Review */}
          <div className="bg-white rounded-lg shadow border overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Items Review</h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Packaging</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {lines.map((line, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {line.original_sku || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {line.original_description || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {line.qty_expected}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {line.packaging_hint || line.expected_package_type || 'Each'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            getCustomerFacingStatusLabel(line, index) === 'Existing SKU'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {getCustomerFacingStatusLabel(line, index)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(inputMethod === 'csv' ? 3 : 2)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-md transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleSubmit}
              disabled={createProjectMutation.isLoading}
              className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white px-6 py-2 rounded-md transition-colors inline-flex items-center"
              type="button"
            >
              {createProjectMutation.isLoading ? 'Submitting...' : 'Submit Manifest'}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CreateInboundProject; 
